// shared/SwotQuadrant.jsx
// SWOT analysis quadrant component for strengths, weaknesses, opportunities, and threats
// Extracted from CompetitiveAnalysisView.jsx for better reusability

import React from 'react';
import { HelpCircle } from 'lucide-react';

/**
 * SWOT analysis quadrant component
 * @param {Object} props - Component props
 * @param {string} props.title - Quadrant title (Strengths, Weaknesses, etc.)
 * @param {Array} props.items - Array of SWOT items to display
 * @param {JSX.Element} props.icon - Icon element for the quadrant
 * @param {string} props.color - Background color class for the quadrant
 * @param {string} props.helpText - Help text for the tooltip
 * @returns {JSX.Element} SWOT quadrant component
 */
const SwotQuadrant = ({ title, items, icon, color, helpText }) => (
    <div className={`p-4 ${color} rounded-lg relative group`}>
        <div className="flex items-center gap-2 mb-3">
            {icon}
            <h4 className="font-medium">{title}</h4>
            <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />

            <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-300 absolute z-10 
                bg-gray-900 text-white text-sm rounded p-2 shadow-lg w-64 top-0 left-1/2 transform -translate-x-1/2">
                <div className="text-xs text-gray-300">{helpText}</div>
            </div>
        </div>
        <ul className="space-y-2">
            {(items || []).map((item, index) => (
                <li key={index} className="text-sm">
                    {item.strength || item.weakness || item.opportunity || item.threat || 'No data available'}
                    {/* Add market details if available */}
                    {(item.strength || item.opportunity) &&
                        (item.strength || item.opportunity).includes('major markets') && (
                            <div className="text-xs mt-1">
                                {item.strength ?
                                    "Markets: China (29.2%), Russia (11.1%)" :
                                    "Markets: Japan (3.4%), South Korea (1.3%)"}
                            </div>
                        )}
                </li>
            ))}
            {(!items || items.length === 0) && (
                <li className="text-sm">No {title.toLowerCase()} identified</li>
            )}
        </ul>
    </div>
);

export default SwotQuadrant;
